import { Link } from "react-router";
import { Table, type TableProps } from "antd";
import { useEffect, useState } from "react";
import { useStore } from "zustand";
import { useDashboardName } from "../../store/headerName";
import ConfirmModal from "../../components/modal/ConfirmModal";
import ButtonIcon from "../../components/ButtonIcon";
import BaseCard from "../../components/card/BaseCard";
import { Icon } from "@iconify/react";
import BaseTable from "../../components/BaseTable";
import {
  formatDate,
  numberFormatter,
  numberFormatterLabel,
} from "../../helper/method";
import { $api, type components } from "../../helper";
import Status from "../../components/Status";
import ButtonLinkIcon from "../../components/ButtonLinkIcon";
import { tokenMock } from "../../helper/mock";
import { useNotify } from "../../helper/notify";
import FilterSearch from "../../components/filter/FilterSearch";
import ButtonIconSvg from "../../components/ButtonIconSvg";
import Trash from "../../components/svg/Trash";
import ButtonLinkIconSvg from "../../components/ButtonLinkIconSvg";
import PenSquare from "../../components/svg/PenSquare";

export default function ManagementDiscountCode() {
  const [isOpen, setIsOpen] = useState(false);
  const notify = useNotify();
  const [id, setId] = useState<string>("");
  const { data, isLoading, refetch } = $api.useQuery(
    "get",
    "/site-api/v1/discount-code/",
  );
  const { mutate, isPending } = $api.useMutation(
    "delete",
    "/site-api/v1/discount-code/",
  );
  const { setDashboardName } = useStore(useDashboardName);
  useEffect(() => {
    setDashboardName("مدیریت کد تخفیف");
  }, []);

  const columns: TableProps<
    components["schemas"]["SiteDiscountCodeItem"]
  >["columns"] = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      render: (_, record, index) => <span>{index + 1}</span>,
    },
    {
      title: "نام کد",
      dataIndex: "name",
      key: "name",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <FilterSearch
          clearFilters={clearFilters}
          confirm={confirm}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
        />
      ),
      onFilter: (value, record) =>
        record?.name
          ?.toLowerCase()
          ?.includes(value.toString().toLocaleLowerCase()),
      filterIcon: () => (
        <Icon className="text-xl" icon={"solar:magnifer-linear"} />
      ),
    },
    {
      title: "کد تخفیف",
      key: "discount_code",
      dataIndex: "discount_code",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <FilterSearch
          clearFilters={clearFilters}
          confirm={confirm}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
        />
      ),
      onFilter: (value, record) =>
        record?.discount_code
          ?.toLowerCase()
          ?.includes(value.toString().toLocaleLowerCase()),
      filterIcon: () => (
        <Icon className="text-xl" icon={"solar:magnifer-linear"} />
      ),
    },
    {
      title: "شروع کد تخفیف",
      key: "start_date",
      dataIndex: "start_date",
      sorter: (a, b) =>
        new Date(a.start_date ?? "").getTime() -
        new Date(b.start_date ?? "").getTime(),
      render: (_, record) => <span>{formatDate(record.start_date ?? "")}</span>,
    },
    {
      title: "پایان کد تخفیف",
      key: "end_date",
      dataIndex: "end_date",
      sorter: (a, b) =>
        new Date(a.end_date ?? "").getTime() -
        new Date(b.end_date ?? "").getTime(),
      render: (_, record) => <span>{formatDate(record.end_date ?? "")}</span>,
    },
    {
      title: "مقدار تخفیف",
      key: "amount",
      dataIndex: "amount",
      render: (_, record) => (
        <span>
          {record.discount_code_type === 1
            ? `% ${numberFormatter(record.amount)}`
            : numberFormatterLabel(record.amount)}
        </span>
      ),
    },
    {
      title: "نوع تخفیف",
      key: "discountCodeType",
      dataIndex: "discountCodeType",
      filters: [
        {
          text: "درصد",
          value: "درصد",
        },
        {
          text: "مقدار",
          value: "مقدار",
        },
      ],
      onFilter: (value, record) =>
        (record.discount_code_type === 1 ? "درصد" : "مقدار").indexOf(
          value as string,
        ) === 0,
      render: (_, record) => (
        <span>{record.discount_code_type === 1 ? "درصد" : "مقدار"}</span>
      ),
    },
    {
      title: "حداکثر تخفیف",
      key: "max_amount",
      dataIndex: "max_amount",
      render: (_, record) => (
        <span>
          {record.max_amount ? numberFormatterLabel(record.max_amount) : "-"}
        </span>
      ),
    },
    {
      title: "وضعیت کد تخفیف",
      key: "status",
      dataIndex: "status",
      filters: [
        {
          text: "فعال",
          value: "فعال",
        },
        {
          text: "غیر فعال",
          value: "غیر فعال",
        },
        {
          text: "منقضی",
          value: "منقضی",
        },
      ],
      onFilter: (value, record) =>
        (record.status === 1
          ? "فعال"
          : record.status === 2
            ? "غیر فعال"
            : "منقضی"
        ).indexOf(value as string) === 0,
      render: (_, record) => (
        <Status
          title={
            record.status === 1
              ? "فعال"
              : record.status === 2
                ? "غیر فعال"
                : "منقضی"
          }
          style={record.status === 1 ? "success" : "error"}
        />
      ),
    },
    {
      title: "عملیات",
      key: "action",
      render: (_, record) => (
        <div className="flex">
          {/* <ButtonLinkIcon
            icon="solar:pen-new-square-linear"
            className="text-primary-500!"
            to={{
              pathname: "/add-discount-code",
              search: "?u_id=" + record.u_id,
            }}
          /> */}
          <ButtonLinkIconSvg
            icon={<PenSquare />}
            className="text-primary-500!"
            to={{
              pathname: "/add-discount-code",
              search: "?u_id=" + record.u_id,
            }}
          />
          {/* <ButtonIcon
            icon="solar:trash-bin-trash-linear"
            className="text-error-500!"
            onClick={() => {
              setIsOpen(true);
              setId(record.u_id);
            }}
          /> */}
          <ButtonIconSvg
            icon={<Trash />}
            className="text-error-500!"
            onClick={() => {
              setIsOpen(true);
              setId(record.u_id);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* modal */}
      <ConfirmModal
        pending={isPending}
        title="حذف کد تخفیف ؟"
        description="حذف این کد تخفیف باعث غیر استفاده شدن میشود."
        action={() =>
          mutate(
            {
              params: { query: { discount_code_u_id: id } },
              headers: { Authorization: tokenMock, lang: "fa" },
            },
            {
              onSuccess: () => {
                setId("");
                setIsOpen(false);
                notify.success(data?.message || "عملیات با موفقیت انجام شد.");
                refetch();
              },
            },
          )
        }
        open={isOpen}
        onClose={() => setIsOpen(false)}
      />
      <div className="w-full flex justify-end sm:flex-row sm:gap-0 gap-4 flex-col sm:items-center items-start">
        <Link to={"/add-discount-code"} className="button-style">
          ایجاد کد تخفیف
        </Link>
      </div>
      <BaseCard props={{ className: "flex-1 flex-col flex gap-6" }}>
        <BaseTable props={{ className: "w-full grid! grid-cols-1!" }}>
          <Table<components["schemas"]["SiteDiscountCodeItem"]>
            columns={columns}
            className="col-span-1!"
            scroll={{ x: "max-content" }}
            dataSource={data?.data}
            loading={isLoading}
          />
        </BaseTable>
      </BaseCard>
    </div>
  );
}
